import React from "react";
import { FormProvider } from "react-hook-form";
import { Link as RouterLink } from "react-router";

import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";

import Button from "components/Button";
import Divider from "components/Divider";
import StyledFormInput from "components/FormInput/FormInput";
import FormNumericInput from "components/FormInput/FormNumericInput";

import AmountBox from "./AmountBox";
import AccountInfo from "./AccountInfo";
import IbanPaymentUrlQrModal from "./IbanPaymentUrlQrModal";

import { routes } from "routes/routes";
import { Bank } from "shared/constants/banks";

import { IbanInvoiceResponseDto } from "api";
import { getPriceFormat } from "shared/functions";
import { FormValues } from "./IbanPaymentForm/validation";
import { useIbanInvoiceForm } from "./IbanPaymentForm/useIbanInvoiceForm";
import { substitutePaymentPurposeTokens } from "shared/functions/ibanQrHelpers";

import { styles } from "./styles";

interface IbanDesktopContentProps {
  handlePayment?: (values?: FormValues, bank?: Bank) => Promise<void>;

  iban: IbanInvoiceResponseDto | null;
  paymentUrl?: string;
}

const IbanDesktopContent: React.FC<IbanDesktopContentProps> = (props) => {
  const { iban, paymentUrl, handlePayment } = props;
  const { amount = 0, paymentPurpose = "", clientNotes = "" } = iban || {};

  const {
    methods,
    showForm,
    showAmountInput,
    showPayerFullName,
    showOrderNumber,
    isLoading,
    onSubmit,
    isPaymentUrlModalOpen,
    onClosePaymentUrlModal,
  } = useIbanInvoiceForm({
    iban,
    handlePayment,
  });

  const formAmount = methods.watch("amount");
  const payerFullName = methods.watch("payerFullName");
  const orderNumber = methods.watch("orderNumber");

  const substitutedPaymentPurpose = substitutePaymentPurposeTokens(
    paymentPurpose,
    payerFullName,
    orderNumber,
  );

  const ibanWithSubstitutedPurpose = iban
    ? { ...iban, paymentPurpose: substitutedPaymentPurpose }
    : null;

  return (
    <Paper elevation={5} sx={styles.paper}>
      <Stack sx={{ gap: 2 }}>
        {!showAmountInput && (
          <AmountBox amount={amount} note={substitutedPaymentPurpose} />
        )}

        {showForm && (
          <FormProvider {...methods}>
            <Stack sx={{ gap: 2 }}>
              {showAmountInput && (
                <FormNumericInput
                  testId="preview-amount"
                  sx={{ mb: 0 }}
                  title="Сума до сплати:"
                  name="amount"
                  showRequired
                  fullWidth
                />
              )}

              {showPayerFullName && (
                <StyledFormInput
                  title="Ім'я та прізвище"
                  name="payerFullName"
                  showRequired
                  slotProps={{ input: { inputProps: { maxLength: 40 } } }}
                  fullWidth
                />
              )}

              {showOrderNumber && (
                <StyledFormInput
                  title="Номер замовлення"
                  name="orderNumber"
                  showRequired
                  slotProps={{ input: { inputProps: { maxLength: 15 } } }}
                  fullWidth
                />
              )}
            </Stack>
          </FormProvider>
        )}

        <Stack>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={onSubmit}
            loading={isLoading}
          >
            Оплатити {getPriceFormat(Number(formAmount) || 0)} ₴
          </Button>
          {showPayerFullName && (
            <Typography
              variant="subtitle2"
              color="grey800"
              sx={{ mt: 1, mb: 1, textAlign: "center" }}
            >
              Продовжуючи, я погоджуюсь з умовами{" "}
              <Link
                component={RouterLink}
                variant="subtitle2"
                to={routes.termsOfService}
              >
                Політики конфіденційності
              </Link>
            </Typography>
          )}
        </Stack>
      </Stack>
      <Divider text="Реквізити" />

      <AccountInfo
        iban={ibanWithSubstitutedPurpose}
        copyEnabled={!!handlePayment}
      />
      <IbanPaymentUrlQrModal
        isOpen={isPaymentUrlModalOpen}
        onCloseModal={onClosePaymentUrlModal}
        clientNotes={clientNotes || ""}
        paymentUrl={paymentUrl}
      />
    </Paper>
  );
};

export default IbanDesktopContent;
